import { useCallback, useEffect, useMemo, useState } from 'react';

import {

  ActionIcon, Alert, Badge, Box, Button, Card, Center, Divider, Flex,

  Grid, Group, Loader, Modal, NumberInput, Pagination, Paper, Progress,

  ScrollArea, Select, SimpleGrid, Stack, Table, Tabs, Text, Textarea,

  TextInput, ThemeIcon, Title, Tooltip,

} from '@mantine/core';

import { useDebouncedValue, useDisclosure } from '@mantine/hooks';

import { notifications } from '@mantine/notifications';

import { BarChart } from '@mantine/charts';

import {

  IconAlertCircle, IconArrowDownRight, IconArrowUpRight, IconBuilding,

  IconCalendarStats, IconCheck, IconChartBar, IconCirclePlus,

  IconCreditCard, IconDownload, IconEdit, IconFileInvoice, IconFilter,

  IconFolder, IconListDetails, IconPlus, IconReceipt, IconSearch,

  IconSettings, IconShieldCheck, IconTrash, IconWallet,

} from '@tabler/icons-react';

import api from '../../api/axiosConfig';

import './it-budget.css';

// SMS Portal'ın ortak Axios istemcisi kullanılır.

// axiosConfig baseURL /api ile bitiyorsa backend /api/it-budget rotaları için

// endpointleri /it-budget ile çağırmalıyız (depo modülündeki gibi).

// baseURL kök adrese işaret ediyorsa /api/it-budget otomatik kullanılır.

const axiosBasePath = (() => {
  // Axios baseURL '/api' ise '/it-budget' kullanılır.
  // Axios doğrudan alan adı/kök dizini kullanıyorsa '/api/it-budget' kullanılır.
  const base = api.defaults.baseURL || '';
  try {
    return new URL(base, window.location.origin).pathname.replace(/\/+$/, '');
  } catch {
    return String(base).replace(/\/+$/, '');
  }
})();

const IT_BUDGET_API = /\/api$/i.test(axiosBasePath) ? '/it-budget' : '/api/it-budget';
const budgetUrl = (path) => `${IT_BUDGET_API}${path}`;


function apiError(error) {

  const detail = error?.response?.data?.detail;

  if (typeof detail === 'string') return detail;

  if (Array.isArray(detail)) return detail.map((item) => item.msg || String(item)).join(', ');

  const status = error?.response?.status;

  if (status === 401) return 'Oturumun süresi dolmuş veya kimlik doğrulaması başarısız.';

  if (status === 403) return 'BT Bütçe modülüne erişim yetkiniz yok.';

  if (status === 404) return 'BT Bütçe API adresi bulunamadı. Router ve Axios baseURL ayarını kontrol edin.';

  return error?.message || 'Sunucuya bağlanılamadı.';

}

async function downloadExpensesExcel(params) {
  try {
    const response = await api.get(budgetUrl('/expenses/export.xlsx'), {
      params,
      responseType: 'blob',
      timeout: 120000,
    });
    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = `bt-butce-gider-raporu-${params.year}.xlsx`;
    document.body.appendChild(link);
    try {
      link.click();
    } finally {
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
    }
  } catch (error) {
    // responseType: blob sebebiyle FastAPI JSON hata mesajlarını ayrıştırıyoruz.
    const data = error?.response?.data;
    if (typeof Blob !== 'undefined' && data instanceof Blob) {
      try {
        const parsed = JSON.parse(await data.text());
        error.response.data = parsed;
      } catch { /* JSON olmayan hata gövdesi */ }
    }
    throw error;
  }
}


const MONTHS = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];

const STATUS = [{ value: 'paid', label: 'Ödendi' }, { value: 'pending', label: 'Ödeme bekliyor' }];

const fmt = (kurus = 0) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 2 }).format(kurus / 100);

const nfmt = (n = 0) => new Intl.NumberFormat('tr-TR').format(n);

const isoLocalDate = () => {

  const d = new Date();

  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;

};

const cashValid = (v) => typeof v === 'number' && Number.isFinite(v) && v >= 0;

const filterOptions = (items) => items.map((item) => ({ value: String(item.id), label: item.name }));

const notifyError = (err) => notifications.show({ title: 'İşlem gerçekleştirilemedi', message: apiError(err), color: 'red' });

const notifySuccess = (msg) => notifications.show({ title: 'İşlem başarılı', message: msg, color: 'teal', icon: <IconCheck size={17} /> });

function StatusPill({ status }) {

  return <Badge color={status === 'paid' ? 'teal' : 'orange'} size="md" radius="sm" variant="light" fw={650}>

    {status === 'paid' ? 'Ödendi' : 'Ödeme bekliyor'}

  </Badge>;

}

function Empty({ text = 'Henüz kayıt bulunmuyor.' }) {

  return <Center py={50}><Stack align="center" gap={9}>

    <ThemeIcon variant="light" color="gray" radius="xl" size={48}><IconFolder size={23} /></ThemeIcon>

    <Text c="dimmed" size="sm">{text}</Text>

  </Stack></Center>;

}

function StatCard({ title, amount, helper, icon: Icon, tint = 'indigo', accent = false, invert = false }) {

  return <Paper radius="lg" p="lg" className={`itb-stat ${accent ? 'itb-stat-accent' : ''}`} withBorder={!accent}>

    <Group align="flex-start" justify="space-between" wrap="nowrap">

      <Stack gap={11} style={{ minWidth: 0 }}>

        <Text size="xs" fw={700} c={accent ? 'indigo.0' : 'dimmed'}>{title}</Text>

        <Text fz={{ base: 22, sm: 25 }} fw={800} lh={1.15} c={accent ? 'white' : invert ? 'red.6' : undefined}>{amount}</Text>

        <Text size="xs" c={accent ? 'indigo.1' : 'dimmed'}>{helper}</Text>

      </Stack>

      <ThemeIcon color={tint} radius="md" size={38} variant={accent ? 'white' : 'light'}><Icon size={19} /></ThemeIcon>

    </Group>

  </Paper>;

}

// Fiyatlar formda KDV hariç TL, API'de kuruş; KDV satır bazında yuvarlanır.

const MAX_INVOICE_KURUS = 1_000_000_000_000_000;

const itemAmounts = (line) => {

  const count = Number.isInteger(line.quantity) && line.quantity >= 1 ? line.quantity : 0;

  const price = typeof line.unit_price_tl === 'number' && Number.isFinite(line.unit_price_tl)

    ? Math.round(line.unit_price_tl * 100) : 0;

  const vatRate = Number.isInteger(line.vat_rate) && line.vat_rate >= 0 && line.vat_rate <= 100

    ? line.vat_rate : 0;

  const net = count * price;

  const vat = Math.floor((net * vatRate + 50) / 100);

  return { net, vat, gross: net + vat };

};

function ExpenseModal({ opened, onClose, initial, categories, locations, onSaved }) {

  const freshLine = () => ({ key: `${Date.now()}-${Math.random()}`, category_id: '',

    description: '', quantity: 1, unit_price_tl: '', vat_rate: 20 });

  const freshInvoice = () => ({ expense_date: isoLocalDate(), location_id: '', description: '',

    vendor: '', invoice_no: '', status: 'pending', items: [freshLine()] });

  const [form, setForm] = useState(freshInvoice);

  const [saving, setSaving] = useState(false);

  useEffect(() => {

    if (!opened) return;

    if (initial) {

      setForm({ expense_date: initial.expense_date, location_id: String(initial.location_id),

        description: initial.description, vendor: initial.vendor || '', invoice_no: initial.invoice_no || '',

        status: initial.status, items: (initial.items || []).map((line, index) => ({

          key: `stored-${line.id || index}`, category_id: String(line.category_id),

          description: line.description, quantity: line.quantity ?? 1,

          unit_price_tl: line.unit_price_kurus / 100, vat_rate: line.vat_rate ?? 20,

        })) });

    } else {

      setForm(freshInvoice());

    }

  }, [opened, initial]);

  const set = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  const changeLine = (key, field, value) => setForm((f) => ({

    ...f, items: f.items.map((line) => line.key === key ? { ...line, [field]: value } : line),

  }));

  const appendLine = () => setForm((f) => ({ ...f, items: [...f.items, freshLine()] }));

  const removeLine = (key) => setForm((f) => ({ ...f, items: f.items.filter((line) => line.key !== key) }));

  const totals = form.items.reduce((sum, line) => {

    const values = itemAmounts(line);

    return { net: sum.net + values.net, vat: sum.vat + values.vat, gross: sum.gross + values.gross };

  }, { net: 0, vat: 0, gross: 0 });

  const submit = async (event) => {

    event.preventDefault();

    if (!form.location_id || !form.expense_date || form.description.trim().length < 2 || !form.items.length ||

        form.items.some((line) => {

          const t = itemAmounts(line);

          return !line.category_id || line.description.trim().length < 2 ||

            !Number.isInteger(line.quantity) || line.quantity < 1 || line.quantity > 100000 ||

            typeof line.unit_price_tl !== 'number' || !Number.isFinite(line.unit_price_tl) ||

            t.net <= 0 || !Number.isSafeInteger(t.net) || !Number.isSafeInteger(t.gross) ||

            !Number.isInteger(line.vat_rate) || line.vat_rate < 0 || line.vat_rate > 100;

        }) || !Number.isSafeInteger(totals.gross) || totals.gross > MAX_INVOICE_KURUS) {

      notifications.show({ title: 'Eksik veya hatalı bilgi',

        message: 'Her kalemde kategori, açıklama, pozitif tam adet, KDV hariç birim fiyat ve 0–100 arası KDV oranı olmalı.',

        color: 'orange' });

      return;

    }

    const payload = {

      expense_date: form.expense_date, location_id: Number(form.location_id),

      description: form.description.trim(), vendor: form.vendor.trim(),

      invoice_no: form.invoice_no.trim(), status: form.status,

      items: form.items.map((line) => ({ category_id: Number(line.category_id),

        description: line.description.trim(), quantity: line.quantity,

        unit_price_kurus: Math.round(line.unit_price_tl * 100), vat_rate: line.vat_rate })),

    };

    setSaving(true);

    try {

      if (initial?.id) await api.put(budgetUrl(`/expenses/${initial.id}`), payload);

      else await api.post(budgetUrl('/expenses'), payload);

      notifySuccess(initial?.id ? 'Fatura ve kalemler güncellendi.' : 'Fatura, adetler ve KDV hesapları kaydedildi.');

      onSaved(); onClose();

    } catch (error) { notifyError(error); } finally { setSaving(false); }

  };

  return <Modal opened={opened} onClose={onClose} title={<Group gap="xs"><IconFileInvoice size={21} />

      <Text fw={750}>{initial ? 'Faturayı düzenle' : 'Yeni fatura ve gider kalemleri'}</Text></Group>}

      centered size="xl" radius="lg" scrollAreaComponent={ScrollArea.Autosize}>

    <form onSubmit={submit}><Stack gap="lg">

      <Paper withBorder radius="md" p="md">

        <Group mb="md" gap="xs"><ThemeIcon color="indigo" variant="light"><IconReceipt size={18}/></ThemeIcon>

          <Text fw={700}>Fatura bilgileri</Text></Group>

        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">

          <TextInput label="Fatura açıklaması" placeholder="Örn. Kasım BT ekipman alımı"

            required value={form.description} onChange={(e) => set('description', e.currentTarget.value)} maxLength={240} />

          <TextInput type="date" label="Fatura tarihi" required value={form.expense_date}

            onChange={(e) => set('expense_date', e.currentTarget.value)} />

          <Select label="Merkez / Şantiye" data={filterOptions(locations)} searchable required

            value={form.location_id} onChange={(v) => set('location_id', v || '')} />

          <Select label="Ödeme durumu" data={STATUS} value={form.status}

            onChange={(v) => set('status', v || 'pending')} allowDeselect={false} />

          <TextInput label="Tedarikçi" placeholder="Örn. BT Tedarik A.Ş."

            value={form.vendor} onChange={(e) => set('vendor', e.currentTarget.value)} maxLength={120} />

          <TextInput label="Fatura numarası" placeholder="Örn. FTR-2026-001"

            value={form.invoice_no} onChange={(e) => set('invoice_no', e.currentTarget.value)} maxLength={100} />

        </SimpleGrid>

      </Paper>

      <Paper withBorder radius="md" p="md">

        <Group justify="space-between" mb="sm">

          <Stack gap={2}><Text fw={750}>Fatura kalemleri</Text>

            <Text size="xs" c="dimmed">Fiyatları KDV hariç girin. Adet ve KDV oranına göre satır toplamı otomatik hesaplanır.</Text></Stack>

          <Badge color="indigo" variant="light">{form.items.length} kalem</Badge>

        </Group>

        <Stack gap="sm">

          {form.items.map((line, index) => {

            const t = itemAmounts(line);

            return <Paper key={line.key} withBorder radius="md" p="sm" bg="gray.0">

              <Group justify="space-between" mb="xs">

                <Text fw={700} size="sm">Kalem {index + 1}</Text>

                <Tooltip label="Kalemi sil"><ActionIcon color="red" variant="subtle"

                  disabled={form.items.length === 1} onClick={() => removeLine(line.key)} aria-label="Kalemi sil">

                  <IconTrash size={17}/></ActionIcon></Tooltip>

              </Group>

              <Grid gutter="sm">

                <Grid.Col span={{ base: 12, sm: 5 }}><Select label="Gider kategorisi" required searchable

                  placeholder="Kategori seç" data={filterOptions(categories)} value={line.category_id}

                  onChange={(v) => changeLine(line.key, 'category_id', v || '')}/></Grid.Col>

                <Grid.Col span={{ base: 12, sm: 7 }}><TextInput label="Ürün / hizmet açıklaması" required

                  placeholder="Örn. Dizüstü bilgisayar" maxLength={240} value={line.description}

                  onChange={(e) => changeLine(line.key, 'description', e.currentTarget.value)}/></Grid.Col>

                <Grid.Col span={{ base: 12, sm: 3 }}><NumberInput label="Adet" required

                  min={1} max={100000} allowDecimal={false} allowNegative={false}

                  value={line.quantity} onChange={(v) => changeLine(line.key, 'quantity', v)}/></Grid.Col>

                <Grid.Col span={{ base: 12, sm: 5 }}><NumberInput label="Birim fiyat (₺, KDV hariç)" required

                  placeholder="0,00" min={0.01} decimalScale={2} thousandSeparator="." decimalSeparator=","

                  allowNegative={false} value={line.unit_price_tl}

                  onChange={(v) => changeLine(line.key, 'unit_price_tl', v)}/></Grid.Col>

                <Grid.Col span={{ base: 12, sm: 4 }}><NumberInput label="KDV oranı (%)" required

                  min={0} max={100} allowDecimal={false} allowNegative={false}

                  value={line.vat_rate} onChange={(v) => changeLine(line.key, 'vat_rate', v)}/></Grid.Col>

              </Grid>

              <Group justify="flex-end" gap="md" mt="sm" wrap="wrap">

                <Text size="xs" c="dimmed">Ara toplam: <Text component="span" fw={650}>{fmt(t.net)}</Text></Text>

                <Text size="xs" c="dimmed">KDV: <Text component="span" fw={650}>{fmt(t.vat)}</Text></Text>

                <Text size="sm" fw={750} c="indigo">KDV dahil: {fmt(t.gross)}</Text>

              </Group>

            </Paper>;

          })}

        </Stack>

        <Group justify="space-between" mt="md" align="flex-start">

          <Button leftSection={<IconPlus size={16}/>} variant="light" onClick={appendLine}

            disabled={form.items.length >= 200}>Yeni kalem ekle</Button>

          <Stack align="flex-end" gap={2}>

            <Text size="sm" c="dimmed">KDV hariç toplam: <Text fw={650} component="span">{fmt(totals.net)}</Text></Text>

            <Text size="sm" c="dimmed">KDV toplamı: <Text fw={650} component="span">{fmt(totals.vat)}</Text></Text>

            <Text fw={900} size="xl" c="indigo">KDV dahil genel toplam: {fmt(totals.gross)}</Text>

          </Stack>

        </Group>

      </Paper>

      <Group justify="flex-end"><Button variant="default" onClick={onClose}>İptal</Button>

        <Button type="submit" loading={saving} leftSection={<IconCheck size={17}/> }>

          {initial ? 'Faturayı güncelle' : 'Faturayı kaydet'}</Button></Group>

    </Stack></form>

  </Modal>;

}

function BudgetModal({ opened, onClose, initial, year, categories, locations, onSaved }) {

  const empty = useMemo(() => ({ year, category_id: categories[0]?.id ? String(categories[0].id) : '',

    location_id: locations[0]?.id ? String(locations[0].id) : '', amount_tl: '', note: '' }), [year, categories, locations]);

  const [form, setForm] = useState(empty);

  const [saving, setSaving] = useState(false);

  useEffect(() => { if (opened) setForm(initial ? { ...initial, category_id: String(initial.category_id),

    location_id: String(initial.location_id), amount_tl: initial.amount_kurus / 100 } : empty); }, [opened, initial, empty]);

  const set = (name, value) => setForm((old) => ({ ...old, [name]: value }));

  const submit = async (event) => {

    event.preventDefault();

    if (!form.category_id || !form.location_id || !cashValid(form.amount_tl)) {

      notifications.show({ message: 'Kategori, lokasyon ve bütçe tutarını belirtin.', color: 'orange' }); return;

    }

    const payload = { year: Number(form.year), category_id: Number(form.category_id), location_id: Number(form.location_id),

      amount_kurus: Math.round(form.amount_tl * 100), note: form.note || '' };

    setSaving(true);

    try {

      if (initial?.id) await api.put(budgetUrl(`/budgets/${initial.id}`), payload);

      else await api.post(budgetUrl('/budgets'), payload);

      notifySuccess('Bütçe planı kaydedildi.'); onSaved(); onClose();

    } catch (err) { notifyError(err); } finally { setSaving(false); }

  };

  return <Modal opened={opened} onClose={onClose} title={initial ? 'Bütçeyi düzenle' : 'Yıllık bütçe tanımla'} centered size="md" radius="lg">

    <form onSubmit={submit}><Stack gap="md">

      <Text size="sm" c="dimmed">Her yıl, lokasyon ve kategori için tek bir bütçe satırı tanımlanır.</Text>

      <NumberInput label="Bütçe yılı" value={form.year} onChange={(v) => set('year', v)} min={2020} max={2100} required allowDecimal={false} />

      <Select label="Kategori" data={filterOptions(categories)} value={form.category_id} onChange={(v) => set('category_id', v)} required searchable />

      <Select label="Lokasyon" data={filterOptions(locations)} value={form.location_id} onChange={(v) => set('location_id', v)} required searchable />

      <NumberInput label="Yıllık bütçe (₺)" value={form.amount_tl} onChange={(v) => set('amount_tl', v)} thousandSeparator="." decimalSeparator="," decimalScale={2} min={0} required />

      <Textarea label="Not" value={form.note} onChange={(e) => set('note', e.currentTarget.value)} maxLength={500} rows={2} placeholder="Opsiyonel açıklama" />

      <Group justify="end" mt="sm"><Button variant="default" onClick={onClose}>Vazgeç</Button><Button type="submit" loading={saving}>Kaydet</Button></Group>

    </Stack></form>

  </Modal>;

}

function Overview({ data, loading, goExpenses, goBudget }) {

  if (loading && !data) return <Center py={75}><Loader /></Center>;

  if (!data) return <Empty text="Özet yüklenemedi." />;

  const monthly = data.monthly.map((x, idx) => ({ month: MONTHS[idx], 'Ödendi': x.paid_kurus / 100,

    'Bekleyen': x.pending_kurus / 100 }));

  return <Stack gap="lg" mt="lg">

    <SimpleGrid cols={{ base: 1, xs: 2, lg: 4 }} spacing="md">

      <StatCard title="YILLIK TOPLAM BÜTÇE" amount={fmt(data.total_budget_kurus)} helper="Planlanan toplam kaynak" icon={IconWallet} accent />

      <StatCard title="GERÇEKLEŞEN GİDER" amount={fmt(data.spent_kurus)} helper={`${nfmt(data.expense_count)} fatura · ${nfmt(data.item_count)} gider kalemi · KDV dahil`} icon={IconReceipt} tint="blue" />

      <StatCard title="KALAN BÜTÇE" amount={fmt(data.remaining_kurus)} helper={data.remaining_kurus < 0 ? 'Bütçe sınırı aşıldı' : 'Kullanılabilir bakiye'} icon={data.remaining_kurus < 0 ? IconArrowDownRight : IconArrowUpRight} tint={data.remaining_kurus < 0 ? 'red' : 'teal'} invert={data.remaining_kurus < 0} />

      <StatCard title="BÜTÇE KULLANIMI" amount={`%${nfmt(data.usage_percent)}`} helper={`${fmt(data.pending_kurus)} ödeme bekliyor`} icon={IconChartBar} tint={data.usage_percent >= 90 ? 'red' : 'violet'} />

    </SimpleGrid>

    {data.total_budget_kurus === 0 && data.spent_kurus > 0 && <Alert icon={<IconAlertCircle size={18} />} color="orange" title="Bütçe tanımlanmamış">Gider kayıtları var ancak seçilen dönem için henüz bütçe belirlenmemiş. <Button variant="subtle" size="xs" onClick={goBudget}>Bütçe ekle</Button></Alert>}

    {data.total_budget_kurus > 0 && data.spent_kurus > data.total_budget_kurus && <Alert icon={<IconAlertCircle size={18} />} color="red" title="Bütçe aşımı">Gerçekleşen ve ödeme bekleyen giderlerin toplamı yıllık bütçeyi aştı.</Alert>}

    <Grid gutter="md">

      <Grid.Col span={{ base: 12, lg: 8 }}>

        <Paper p="lg" radius="lg" withBorder className="itb-paper">

          <Group justify="space-between" mb="lg"><Stack gap={3}><Title order={4}>Aylık gider akışı</Title><Text size="xs" c="dimmed">Ödenen ve ödeme bekleyen giderlerin dağılımı</Text></Stack><Badge variant="light" color="indigo">12 ay</Badge></Group>

          <BarChart h={278} data={monthly} dataKey="month" type="stacked" withLegend tickLine="y" gridAxis="y"

            series={[{ name: 'Ödendi', color: 'indigo.6' }, { name: 'Bekleyen', color: 'orange.4' }]}

            valueFormatter={(value) => new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 0 }).format(value) + ' ₺'} />

        </Paper>

      </Grid.Col>

      <Grid.Col span={{ base: 12, lg: 4 }}>

        <Paper p="lg" radius="lg" withBorder className="itb-paper" h="100%">

          <Group justify="space-between" mb="sm"><Stack gap={3}><Title order={4}>Bütçe sağlığı</Title><Text size="xs" c="dimmed">Toplam limit ve harcama oranı</Text></Stack><ThemeIcon variant="light" color="indigo" size={36}><IconShieldCheck size={19} /></ThemeIcon></Group>

          <Center mt={25}><Box className="itb-ring" style={{ '--pct': `${Math.min(100, data.usage_percent)}%`, '--ringColor': data.usage_percent > 100 ? '#e05252' : '#4f46e5' }}><Stack gap={0} align="center"><Text fz={30} fw={800}>%{Math.round(data.usage_percent)}</Text><Text size="xs" c="dimmed">kullanıldı</Text></Stack></Box></Center>

          <Divider my="lg" />

          <Group justify="space-between"><Text size="sm" c="dimmed">Ödenen gider</Text><Text fw={700} size="sm">{fmt(data.paid_kurus)}</Text></Group>

          <Group justify="space-between" mt="sm"><Text size="sm" c="dimmed">Ödeme bekleyen</Text><Text fw={700} size="sm">{fmt(data.pending_kurus)}</Text></Group>

        </Paper>

      </Grid.Col>

    </Grid>

    <Grid gutter="md">

      <Grid.Col span={{ base: 12, md: 6 }}><Paper p="lg" withBorder radius="lg" className="itb-paper" h="100%">

        <Group justify="space-between" mb="lg"><Title order={4}>Kategori performansı</Title><Text size="xs" c="dimmed">Bütçe / Gider</Text></Group>

        {data.categories.length ? <Stack gap="lg">{[...data.categories].sort((a,b) => b.spent_kurus - a.spent_kurus).map((c) => {

          const percentage = c.budget_kurus ? c.spent_kurus / c.budget_kurus * 100 : c.spent_kurus ? 100 : 0;

          return <Box key={c.category_id}><Group justify="space-between" mb={5} align="flex-start"><Group gap={7} wrap="nowrap"><Box style={{ width: 9, height: 9, borderRadius: 3, background: c.color, flexShrink: 0 }} /><Text size="sm" fw={650}>{c.name}</Text></Group><Text size="xs" fw={700}>{fmt(c.spent_kurus)} <Text span c="dimmed" fw={400}>/ {fmt(c.budget_kurus)}</Text></Text></Group>

          <Progress radius="xl" size={8} value={Math.min(100, percentage)} color={percentage > 100 ? 'red' : c.color} />

          {percentage > 100 && <Text c="red" size="xs" mt={2}>{c.budget_kurus ? 'Limit aşıldı' : 'Bu kategoriye bütçe tanımlanmadı'}</Text>}

          </Box>;

        })}</Stack> : <Empty text="Kategori bazlı bütçe veya gider bulunmuyor." />}

      </Paper></Grid.Col>

      <Grid.Col span={{ base: 12, md: 6 }}><Paper p="lg" withBorder radius="lg" className="itb-paper" h="100%">

        <Group justify="space-between" mb="lg"><Title order={4}>Merkez ve şantiye dağılımı</Title><ThemeIcon size={32} color="gray" variant="light"><IconBuilding size={18} /></ThemeIcon></Group>

        {data.locations.length ? <Stack gap="md">{data.locations.map((loc) => <Box key={loc.location_id}>

          <Group justify="space-between" mb="xs"><Group gap="xs"><Text fw={650} size="sm">{loc.name}</Text>{loc.kind === 'hq' && <Badge size="xs" variant="light">Merkez</Badge>}</Group><Text fw={750} size="sm">{fmt(loc.spent_kurus)}</Text></Group>

          <Progress size={8} radius="xl" color={loc.budget_kurus && loc.spent_kurus > loc.budget_kurus ? 'red' : 'indigo'} value={loc.budget_kurus ? Math.min(loc.spent_kurus / loc.budget_kurus * 100, 100) : loc.spent_kurus ? 100 : 0} />

          <Text size="xs" c="dimmed" mt={5}>Bütçe: {fmt(loc.budget_kurus)}</Text>

        </Box>)}</Stack> : <Empty text="Lokasyon bazlı kayıt bulunmuyor." />}

      </Paper></Grid.Col>

    </Grid>

    <Paper p="lg" radius="lg" withBorder className="itb-paper">

      <Group justify="space-between" mb="md"><Stack gap={2}><Title order={4}>Son gider hareketleri</Title><Text size="xs" c="dimmed">En son kaydedilmiş giderler</Text></Stack><Button variant="subtle" size="xs" onClick={goExpenses}>Tümünü gör</Button></Group>

      {data.recent.length ? <ScrollArea><Table horizontalSpacing="md" verticalSpacing="md" miw={690} highlightOnHover><Table.Thead><Table.Tr><Table.Th>Tarih</Table.Th><Table.Th>Açıklama</Table.Th><Table.Th>Kalemler</Table.Th><Table.Th>Lokasyon</Table.Th><Table.Th>Durum</Table.Th><Table.Th style={{ textAlign: 'right' }}>Tutar</Table.Th></Table.Tr></Table.Thead><Table.Tbody>

        {data.recent.map((x) => <Table.Tr key={x.id}><Table.Td><Text size="sm">{x.expense_date}</Text></Table.Td><Table.Td><Text size="sm" fw={600}>{x.description}</Text><Text size="xs" c="dimmed">{x.vendor || '—'}</Text></Table.Td><Table.Td><Text size="sm">{x.item_count} kalem / {x.items.map((i) => i.category_name).filter((v, j, arr) => arr.indexOf(v) === j).join(", ")}</Text></Table.Td><Table.Td><Text size="sm">{x.location_name}</Text></Table.Td><Table.Td><StatusPill status={x.status} /></Table.Td><Table.Td align="right"><Text fw={750} size="sm">{fmt(x.amount_kurus)}</Text></Table.Td></Table.Tr>)}

      </Table.Tbody></Table></ScrollArea> : <Empty text="İlk giderinizi ekleyerek takibe başlayın." />}

    </Paper>

  </Stack>;

}

function Expenses({ data, loading, categories, categoryFilter, setCategoryFilter, statusFilter, setStatusFilter,

  search, setSearch, page, setPage, onEdit, onDelete }) {

  const [detail, setDetail] = useState(null);

  return <Paper p="lg" radius="lg" withBorder mt="lg" className="itb-paper">

    <Group justify="space-between" mb="lg"><Stack gap={3}><Title order={4}>Faturalar ve gider kalemleri</Title>

      <Text size="xs" c="dimmed">Kalemlerde KDV hariç birim fiyat; fatura toplamında KDV dahil tutar kullanılır.</Text></Stack>

      <Badge size="lg" variant="light" color="indigo">{nfmt(data?.total || 0)} fatura</Badge></Group>

    <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} mb="lg" spacing="sm">

      <TextInput leftSection={<IconSearch size={16}/>} placeholder="Fatura, tedarikçi veya kalem açıklaması ara"

        value={search} onChange={(e) => { setSearch(e.currentTarget.value); setPage(1); }}/>

      <Select leftSection={<IconFilter size={16}/>} placeholder="Tüm kategoriler" clearable

        data={filterOptions(categories)} value={categoryFilter} onChange={(v) => { setCategoryFilter(v); setPage(1); }}/>

      <Select clearable placeholder="Tüm ödeme durumları" data={STATUS} value={statusFilter}

        onChange={(v) => { setStatusFilter(v); setPage(1); }}/>

    </SimpleGrid>

    {loading ? <Center py={60}><Loader/></Center> : data?.items?.length ? <>

      <ScrollArea><Table highlightOnHover verticalSpacing="md" horizontalSpacing="md" miw={980}>

        <Table.Thead><Table.Tr><Table.Th>Tarih</Table.Th><Table.Th>Fatura / Tedarikçi</Table.Th>

          <Table.Th>Kalemler ve kategoriler</Table.Th><Table.Th>Lokasyon</Table.Th>

          <Table.Th>Ödeme</Table.Th><Table.Th>KDV dahil toplam</Table.Th><Table.Th ta="right">İşlemler</Table.Th>

        </Table.Tr></Table.Thead>

        <Table.Tbody>{data.items.map((invoice) => <Table.Tr key={invoice.id}>

          <Table.Td><Text size="sm">{invoice.expense_date}</Text></Table.Td>

          <Table.Td><Text fw={650} size="sm">{invoice.description}</Text>

            <Text c="dimmed" size="xs">{invoice.vendor || 'Tedarikçi belirtilmedi'}

              {invoice.invoice_no ? ` · ${invoice.invoice_no}` : ''}</Text></Table.Td>

          <Table.Td><Stack gap={4}>{invoice.items.slice(0, 3).map((line) =>

            <Group key={line.id} gap={5} wrap="nowrap"><Badge variant="light" color="gray" size="xs">{line.category_name}</Badge>

              <Text size="xs" lineClamp={1}>{line.description} · {line.quantity} adet</Text></Group>)}

            {invoice.items.length > 3 && <Text size="xs" c="dimmed">+{invoice.items.length - 3} kalem daha</Text>}

          </Stack></Table.Td>

          <Table.Td><Text size="sm">{invoice.location_name}</Text></Table.Td>

          <Table.Td><StatusPill status={invoice.status}/></Table.Td>

          <Table.Td><Text fw={750} size="sm" style={{ whiteSpace: 'nowrap' }}>{fmt(invoice.amount_kurus)}</Text></Table.Td>

          <Table.Td><Group justify="end" gap={5} wrap="nowrap">

            <Tooltip label="Fatura kalemlerini gör"><ActionIcon variant="subtle" color="blue"

              onClick={() => setDetail(invoice)} aria-label="Fatura detayını görüntüle"><IconListDetails size={17}/></ActionIcon></Tooltip>

            <Tooltip label="Faturayı düzenle"><ActionIcon variant="subtle" onClick={() => onEdit(invoice)}

              aria-label="Faturayı düzenle"><IconEdit size={17}/></ActionIcon></Tooltip>

            <Tooltip label="Faturayı sil"><ActionIcon color="red" variant="subtle" onClick={() => onDelete(invoice)}

              aria-label="Faturayı sil"><IconTrash size={17}/></ActionIcon></Tooltip>

          </Group></Table.Td>

        </Table.Tr>)}</Table.Tbody>

      </Table></ScrollArea>

      {data.total > data.page_size && <Group justify="flex-end" mt="lg"><Pagination value={page}

        onChange={setPage} total={Math.ceil(data.total / data.page_size)} size="sm"/></Group>}

    </> : <Empty text="Henüz kayıtlı fatura yok."/>}

    <Modal opened={!!detail} onClose={() => setDetail(null)} title="Fatura detayları" size="lg" centered>

      {detail && <Stack gap="md">

        <SimpleGrid cols={2}><Box><Text size="xs" c="dimmed">Fatura</Text>

          <Text fw={700}>{detail.invoice_no || `#${detail.id}`}</Text></Box>

          <Box><Text size="xs" c="dimmed">Tedarikçi</Text><Text fw={700}>{detail.vendor || '—'}</Text></Box>

          <Box><Text size="xs" c="dimmed">Tarih</Text><Text size="sm">{detail.expense_date}</Text></Box>

          <Box><Text size="xs" c="dimmed">Lokasyon</Text><Text size="sm">{detail.location_name}</Text></Box>

        </SimpleGrid>

        <Divider/>

        <ScrollArea><Table striped verticalSpacing="sm" miw={800}>

          <Table.Thead><Table.Tr><Table.Th>Kalem</Table.Th><Table.Th>Kategori</Table.Th>

            <Table.Th ta="right">Adet</Table.Th><Table.Th ta="right">Birim (KDV hariç)</Table.Th>

            <Table.Th ta="right">Ara toplam</Table.Th><Table.Th ta="right">KDV %</Table.Th>

            <Table.Th ta="right">KDV</Table.Th><Table.Th ta="right">KDV dahil</Table.Th>

          </Table.Tr></Table.Thead>

          <Table.Tbody>{detail.items.map((line) => <Table.Tr key={line.id}>

            <Table.Td>{line.description}</Table.Td><Table.Td>{line.category_name}</Table.Td>

            <Table.Td ta="right">{line.quantity}</Table.Td>

            <Table.Td ta="right">{fmt(line.unit_price_kurus)}</Table.Td>

            <Table.Td ta="right">{fmt(line.net_amount_kurus)}</Table.Td>

            <Table.Td ta="right">%{line.vat_rate}</Table.Td>

            <Table.Td ta="right">{fmt(line.vat_amount_kurus)}</Table.Td>

            <Table.Td ta="right">{fmt(line.amount_kurus)}</Table.Td>

          </Table.Tr>)}</Table.Tbody>

        </Table></ScrollArea>

        <Stack gap={3} align="flex-end">

          <Text size="sm">KDV hariç toplam: <Text fw={650} component="span">{fmt(detail.net_amount_kurus)}</Text></Text>

          <Text size="sm">KDV toplamı: <Text fw={650} component="span">{fmt(detail.vat_amount_kurus)}</Text></Text>

          <Text size="lg" fw={850}>KDV dahil genel toplam: {fmt(detail.amount_kurus)}</Text>

        </Stack>

      </Stack>}

    </Modal>

  </Paper>;

}

// Fatura kalemlerine dokunmadan yalnızca ödeme durumunu yönetmek için ayrı çalışma alanı.
function PendingPayments({ data, loading, error, totalPendingKurus, search, setSearch,
  page, setPage, onMarkPaid }) {
  return <Stack gap="lg" mt="lg">
    <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
      <Paper p="lg" withBorder radius="lg" className="itb-stat">
        <Group justify="space-between" align="flex-start">
          <Stack gap={8}>
            <Text size="xs" fw={700} c="dimmed">ÖDEME BEKLEYEN TOPLAM TUTAR</Text>
            <Text size="xl" fw={800} c="orange.8">{fmt(totalPendingKurus)}</Text>
            <Text size="xs" c="dimmed">Seçilen yıl ve lokasyonun tamamı; arama filtresinden bağımsız</Text>
          </Stack>
          <ThemeIcon size={40} radius="md" variant="light" color="orange"><IconCreditCard size={21}/></ThemeIcon>
        </Group>
      </Paper>
      <Paper p="lg" withBorder radius="lg" className="itb-stat">
        <Group justify="space-between" align="flex-start">
          <Stack gap={8}>
            <Text size="xs" fw={700} c="dimmed">BEKLEYEN FATURA SAYISI</Text>
            <Text size="xl" fw={800}>{nfmt(data?.total || 0)}</Text>
            <Text size="xs" c="dimmed">Listeye uygulanan aramaya göre fatura sayısı</Text>
          </Stack>
          <ThemeIcon size={40} radius="md" variant="light" color="indigo"><IconFileInvoice size={21}/></ThemeIcon>
        </Group>
      </Paper>
    </SimpleGrid>

    <Paper p="lg" withBorder radius="lg" className="itb-paper">
      <Group justify="space-between" align="flex-start" mb="lg">
        <Stack gap={3}>
          <Title order={4}>Ödeme bekleyen faturalar</Title>
          <Text c="dimmed" size="xs">Faturayı düzenlemeden, yalnızca ödeme durumunu güncelleyin.</Text>
        </Stack>
        <Badge variant="light" color="orange" size="lg">Ödeme takibi</Badge>
      </Group>
      <TextInput mb="md" leftSection={<IconSearch size={16}/>} placeholder="Fatura numarası, açıklama, tedarikçi veya kalem ara"
        value={search} onChange={(event) => { setSearch(event.currentTarget.value); setPage(1); }} />
      {error && <Alert mb="md" color="red" icon={<IconAlertCircle size={17}/>} title="Bekleyen faturalar alınamadı">{error}</Alert>}
      {loading ? <Center py={70}><Loader/></Center> : data?.items?.length ? <>
        <ScrollArea>
          <Table verticalSpacing="md" horizontalSpacing="md" highlightOnHover miw={890}>
            <Table.Thead><Table.Tr>
              <Table.Th>Fatura tarihi</Table.Th>
              <Table.Th>Fatura / Tedarikçi</Table.Th>
              <Table.Th>Merkez / Şantiye</Table.Th>
              <Table.Th>Kalem</Table.Th>
              <Table.Th ta="right">KDV dahil tutar</Table.Th>
              <Table.Th ta="right">İşlem</Table.Th>
            </Table.Tr></Table.Thead>
            <Table.Tbody>
              {data.items.map((invoice) => <Table.Tr key={invoice.id}>
                <Table.Td><Text size="sm">{invoice.expense_date}</Text></Table.Td>
                <Table.Td>
                  <Text size="sm" fw={700}>{invoice.description}</Text>
                  <Text size="xs" c="dimmed">{invoice.vendor || 'Tedarikçi belirtilmemiş'}</Text>
                  <Text size="xs" c="dimmed">{invoice.invoice_no || `Fatura #${invoice.id}`}</Text>
                </Table.Td>
                <Table.Td><Text size="sm">{invoice.location_name}</Text></Table.Td>
                <Table.Td><Badge size="sm" variant="light" color="gray">{invoice.item_count} kalem</Badge></Table.Td>
                <Table.Td ta="right"><Text fw={800} size="sm" style={{whiteSpace:'nowrap'}}>{fmt(invoice.amount_kurus)}</Text></Table.Td>
                <Table.Td ta="right">
                  <Button size="xs" color="teal" variant="light" leftSection={<IconCheck size={15}/>}
                    onClick={() => onMarkPaid(invoice)}>Ödendi olarak işaretle</Button>
                </Table.Td>
              </Table.Tr>)}
            </Table.Tbody>
          </Table>
        </ScrollArea>
        {data.total > data.page_size && <Group mt="lg" justify="flex-end">
          <Pagination value={page} onChange={setPage} size="sm" total={Math.ceil(data.total / data.page_size)} />
        </Group>}
      </> : !error && <Empty text={search ? 'Aramanızla eşleşen bekleyen fatura bulunmuyor.' : 'Bu dönem için bekleyen fatura bulunmuyor.'}/>}
    </Paper>
  </Stack>;
}

function Budgets({ data, loading, onEdit, onDelete }) {

  const budget = data.reduce((n,x) => n+x.amount_kurus, 0);

  const spent = data.reduce((n,x) => n+x.spent_kurus, 0);

  return <Stack gap="md" mt="lg">

    <SimpleGrid cols={{ base: 1, sm: 3 }}>

      <StatCard title="PLANLANAN" amount={fmt(budget)} helper="Seçili bütçe kalemleri" icon={IconWallet} />

      <StatCard title="HARCANAN" amount={fmt(spent)} helper="Aynı kategori ve lokasyondaki giderler" icon={IconReceipt} tint="blue" />

      <StatCard title="KALAN" amount={fmt(budget-spent)} helper="Negatif değer aşım anlamına gelir" icon={IconChartBar} tint={budget-spent < 0 ? 'red' : 'teal'} invert={budget-spent < 0} />

    </SimpleGrid>

    <Paper p="lg" radius="lg" withBorder className="itb-paper">

      <Group justify="space-between" mb="lg"><Title order={4}>Yıllık bütçe planı</Title><Text size="xs" c="dimmed">Kategori × Lokasyon</Text></Group>

      {loading ? <Center py={60}><Loader /></Center> : data.length ? <ScrollArea><Table miw={780} highlightOnHover verticalSpacing="md">

        <Table.Thead><Table.Tr><Table.Th>Kategori</Table.Th><Table.Th>Lokasyon</Table.Th><Table.Th>Bütçe</Table.Th><Table.Th>Gider</Table.Th><Table.Th>Kalan</Table.Th><Table.Th w={140}>Kullanım</Table.Th><Table.Th ta="right">İşlem</Table.Th></Table.Tr></Table.Thead>

        <Table.Tbody>{data.map((b) => {

          const use = b.amount_kurus ? 100*b.spent_kurus/b.amount_kurus : b.spent_kurus ? 101 : 0;

          return <Table.Tr key={b.id}>

            <Table.Td><Text fw={650} size="sm">{b.category_name}</Text>{b.note && <Text size="xs" c="dimmed" lineClamp={1}>{b.note}</Text>}</Table.Td>

            <Table.Td><Text size="sm">{b.location_name}</Text></Table.Td>

            <Table.Td><Text size="sm">{fmt(b.amount_kurus)}</Text></Table.Td>

            <Table.Td><Text size="sm">{fmt(b.spent_kurus)}</Text></Table.Td>

            <Table.Td><Text fw={700} c={b.remaining_kurus < 0 ? 'red' : undefined} size="sm">{fmt(b.remaining_kurus)}</Text></Table.Td>

            <Table.Td><Stack gap={5}><Progress value={Math.min(100, use)} size={7} color={use > 100 ? 'red' : use >= 90 ? 'orange' : 'indigo'} /><Text size="xs" c="dimmed">%{use.toFixed(1)}</Text></Stack></Table.Td>

            <Table.Td><Group gap={5} justify="end" wrap="nowrap"><ActionIcon variant="subtle" onClick={() => onEdit(b)} aria-label="Bütçeyi düzenle"><IconEdit size={17} /></ActionIcon><ActionIcon variant="subtle" color="red" onClick={() => onDelete(b)} aria-label="Bütçeyi sil"><IconTrash size={17} /></ActionIcon></Group></Table.Td>

          </Table.Tr>;

        })}</Table.Tbody>

      </Table></ScrollArea> : <Empty text="Bu dönem için henüz bütçe planı eklenmedi." />}

    </Paper>

  </Stack>;

}

function Definitions({ categories, locations, onChanged }) {

  const [edit, setEdit] = useState(null);

  const [name, setName] = useState('');

  const [color, setColor] = useState('#4978e5');

  const [saving, setSaving] = useState(false);

  const openForm = (type, item=null) => { setEdit({ type, item }); setName(item?.name || ''); setColor(item?.color || '#4978e5'); };

  const save = async (e) => {

    e.preventDefault(); if (name.trim().length < 2) return;

    setSaving(true);

    try {

      const url = '/categories';

      const payload = { name: name.trim(), color };

      if (edit.item?.id) await api.put(budgetUrl(`${url}/${edit.item.id}`), payload);

      else await api.post(budgetUrl(url), payload);

      notifySuccess('Tanım kaydedildi.'); setEdit(null); onChanged();

    } catch(err) { notifyError(err); } finally { setSaving(false); }

  };

  const remove = async (type, item) => {

    if (!window.confirm(`${item.name} kaydını silmek istediğinize emin misiniz? Kullanılan tanımlar silinemez.`)) return;

    try { await api.delete(budgetUrl(`/${type}/${item.id}`)); notifySuccess('Tanım silindi.'); onChanged(); }

    catch(err) { notifyError(err); }

  };

  return <>

    <Grid gutter="md" mt="lg">

      <Grid.Col span={{ base: 12, md: 6 }}><Paper withBorder p="lg" radius="lg" className="itb-paper"><Group justify="space-between" mb="md"><Title order={4}>Gider kategorileri</Title><Button size="xs" leftSection={<IconPlus size={15} />} variant="light" onClick={() => openForm('categories')}>Kategori ekle</Button></Group>

        <Stack gap={4}>{categories.map((c) => <Group key={c.id} justify="space-between" className="itb-setting-row"><Group gap="xs"><Box w={12} h={12} style={{ borderRadius: 4, background: c.color }} /><Text size="sm" fw={600}>{c.name}</Text></Group><Group gap={4}><ActionIcon variant="subtle" onClick={() => openForm('categories', c)}><IconEdit size={17} /></ActionIcon><ActionIcon variant="subtle" color="red" onClick={() => remove('categories', c)}><IconTrash size={17} /></ActionIcon></Group></Group>)}</Stack>

      </Paper></Grid.Col>

      <Grid.Col span={{ base: 12, md: 6 }}><Paper withBorder p="lg" radius="lg" className="itb-paper"><Group justify="space-between" mb="md"><Title order={4}>Merkez ve şantiyeler</Title><Badge variant="light" color="blue">Portal kayıtları</Badge></Group>

        <Text size="xs" c="dimmed" mb="sm">Lokasyonlar İK merkez tanımları ve Şantiye Yönetimi kayıtlarından otomatik alınır. Buradan değiştirilmez.</Text>

        <Stack gap={4}>{locations.length ? locations.map((l) => <Group key={l.id} justify="space-between" className="itb-setting-row"><Group gap="xs"><ThemeIcon color="gray" variant="light" size={26}><IconBuilding size={16} /></ThemeIcon><Text size="sm" fw={600}>{l.name}</Text>{l.kind === 'hq' && <Badge variant="light" size="xs">Merkez</Badge>}</Group><Badge variant="outline" size="xs">{l.kind === 'hq' ? 'Merkez' : 'Şantiye'}</Badge></Group>) : <Text size="sm" c="dimmed">Merkez veya şantiye kaydı bulunamadı.</Text>}</Stack>

      </Paper></Grid.Col>

    </Grid>

    <Modal opened={!!edit} onClose={() => setEdit(null)} title={edit?.item ? 'Tanımı düzenle' : 'Yeni tanım ekle'} centered radius="lg">

      <form onSubmit={save}><Stack gap="md"><TextInput label="Ad" value={name} onChange={(e) => setName(e.currentTarget.value)} minLength={2} maxLength={100} required />

        <Group><TextInput label="Renk (#RRGGBB)" value={color} onChange={(e) => setColor(e.currentTarget.value)} required /><Box mt={20} w={25} h={25} style={{ borderRadius: 6, backgroundColor: /^#[0-9a-f]{6}$/i.test(color) ? color : '#ddd' }} /></Group>

        <Group justify="end"><Button variant="default" onClick={() => setEdit(null)}>Vazgeç</Button><Button type="submit" loading={saving}>Kaydet</Button></Group>

      </Stack></form>

    </Modal>

  </>;

}

export default function ITBudgetModule() {

  const [tab, setTab] = useState('overview');

  const [year, setYear] = useState(new Date().getFullYear());

  const [locationId, setLocationId] = useState(null);

  const [categories, setCategories] = useState([]);

  const [locations, setLocations] = useState([]);

  const [dashboard, setDashboard] = useState(null);

  const [budgets, setBudgets] = useState([]);

  const [expenses, setExpenses] = useState(null);

  const [isLoading, setIsLoading] = useState(true);

  const [reload, setReload] = useState(0);

  const [search, setSearch] = useState('');

  const [debouncedSearch] = useDebouncedValue(search, 350);

  const [categoryFilter, setCategoryFilter] = useState(null);

  const [statusFilter, setStatusFilter] = useState(null);

  const [page, setPage] = useState(1);

  const [pendingData, setPendingData] = useState(null);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [pendingError, setPendingError] = useState('');
  const [pendingPage, setPendingPage] = useState(1);
  const [pendingSearch, setPendingSearch] = useState('');
  const [debouncedPendingSearch] = useDebouncedValue(pendingSearch, 350);
  const [paymentInvoice, setPaymentInvoice] = useState(null);
  const [paymentSaving, setPaymentSaving] = useState(false);


  const [expenseOpened, { open: openExpense, close: closeExpense }] = useDisclosure(false);

  const [budgetOpened, { open: openBudget, close: closeBudget }] = useDisclosure(false);

  const [editingExpense, setEditingExpense] = useState(null);

  const [editingBudget, setEditingBudget] = useState(null);

  const [fatal, setFatal] = useState('');
  const [exportingExcel, setExportingExcel] = useState(false);

  const refresh = () => setReload((i) => i+1);

  useEffect(() => {

    let cancelled = false;

    Promise.all([api.get(budgetUrl('/categories')), api.get(budgetUrl('/locations'))])

      .then(([c, l]) => { if (!cancelled) { setCategories(c.data); setLocations(l.data); } })

      .catch((e) => { if (!cancelled) setFatal(apiError(e)); });

    return () => { cancelled = true; };

  }, [reload]);

  useEffect(() => {

    let cancelled = false;

    setIsLoading(true);

    setFatal('');

    const params = { year, ...(locationId ? { location_id: Number(locationId) } : {}) };

    Promise.all([

      api.get(budgetUrl('/dashboard'), { params }),

      api.get(budgetUrl('/budgets'), { params }),

      api.get(budgetUrl('/expenses'), { params: { ...params, page, page_size: 20,

        ...(categoryFilter ? { category_id: Number(categoryFilter) } : {}),

        ...(statusFilter ? { status: statusFilter } : {}),

        ...(debouncedSearch.trim() ? { q: debouncedSearch.trim() } : {}) } }),

    ]).then(([d, b, e]) => { if (!cancelled) { setDashboard(d.data); setBudgets(b.data); setExpenses(e.data); } })

      .catch((e) => { if (!cancelled) setFatal(apiError(e)); })

      .finally(() => { if (!cancelled) setIsLoading(false); });

    return () => { cancelled = true; };

  }, [year, locationId, reload, page, categoryFilter, statusFilter, debouncedSearch]);

  // Genel fatura listesinden bağımsız: yalnızca bu sekme açıkken bekleyenleri yükle.
  useEffect(() => {
    if (tab !== 'pending') return;
    let cancelled = false;
    setPendingLoading(true);
    setPendingError('');
    const params = {
      year,
      status: 'pending',
      page: pendingPage,
      page_size: 20,
      ...(locationId ? { location_id: Number(locationId) } : {}),
      ...(debouncedPendingSearch.trim() ? { q: debouncedPendingSearch.trim() } : {}),
    };
    api.get(budgetUrl('/expenses'), { params })
      .then(({ data }) => { if (!cancelled) setPendingData(data); })
      .catch((error) => { if (!cancelled) { setPendingError(apiError(error)); setPendingData(null); } })
      .finally(() => { if (!cancelled) setPendingLoading(false); });
    return () => { cancelled = true; };
  }, [tab, year, locationId, pendingPage, debouncedPendingSearch, reload]);

  const markPaid = async () => {
    if (!paymentInvoice || paymentSaving) return;
    setPaymentSaving(true);
    try {
      // Sadece durum değişir. Tüm kalemlerin PUT ile yeniden gönderilmesi gerekmez.
      await api.patch(budgetUrl(`/expenses/${paymentInvoice.id}/mark-paid`));
      notifySuccess(`Fatura ${paymentInvoice.invoice_no || `#${paymentInvoice.id}`} ödendi olarak işaretlendi.`);
      setPaymentInvoice(null);
      const isLastOnPage = pendingData?.items?.length === 1 && pendingPage > 1;
      if (isLastOnPage) setPendingPage((p) => p - 1);
      refresh();
    } catch (error) { notifyError(error); }
    finally { setPaymentSaving(false); }
  };

  const doDelete = async (entity, item) => {

    if (!window.confirm(`Bu ${entity === 'expenses' ? 'faturayı ve tüm kalemlerini' : 'bütçeyi'} silmek istiyor musunuz? İşlem değişiklik geçmişine kaydedilecektir.`)) return;

    try { await api.delete(budgetUrl(`/${entity}/${item.id}`)); notifySuccess('Kayıt silindi.'); refresh(); }

    catch(err) { notifyError(err); }

  };

  const exportExcel = async () => {
    if (exportingExcel) return;
    setExportingExcel(true);
    try {
      await downloadExpensesExcel({
        year,
        ...(locationId ? { location_id: Number(locationId) } : {}),
        ...(categoryFilter ? { category_id: Number(categoryFilter) } : {}),
        ...(statusFilter ? { status: statusFilter } : {}),
        ...(debouncedSearch.trim() ? { q: debouncedSearch.trim() } : {}),
      });
      notifySuccess('Biçimlendirilmiş Excel raporu indirildi.');
    } catch (err) {
      notifyError(err);
    } finally {
      setExportingExcel(false);
    }
  };

  const years = Array.from({ length: new Date().getFullYear() - 2020 + 4 }, (_, i) => new Date().getFullYear() + 3 - i);

  return <Box className="itb-root">

    <Box className="itb-container">

      <Group justify="space-between" align="flex-start" mb="xl" gap="md">

        <Group align="flex-start" gap="md">

          <ThemeIcon size={48} radius="lg" className="itb-brand"><IconWallet size={25} /></ThemeIcon>

          <Stack gap={3}><Text className="itb-eyebrow">FİNANSAL YÖNETİM / BİLGİ İŞLEM</Text><Title order={2} className="itb-main-title">BT Bütçe ve Gider Yönetimi</Title><Text c="dimmed" size="sm">Merkez ve şantiyeler için planlanan bütçeler, gerçekleşen maliyetler ve ödeme takibi.</Text></Stack>

        </Group>

        <Group gap="sm">

          <Tooltip label="Seçili filtrelerle çok sayfalı Excel raporu indir"><Button leftSection={<IconDownload size={17} />} variant="default" color="indigo" loading={exportingExcel} onClick={exportExcel}>Excel dışa aktar</Button></Tooltip>

          <Button leftSection={<IconPlus size={17} />} onClick={() => { setEditingExpense(null); openExpense(); }}>Fatura ekle</Button>

        </Group>

      </Group>

      <Paper withBorder p="sm" radius="lg" className="itb-toolbar" mb="lg">

        <Group justify="space-between" gap="sm"><Group gap="sm">

          <Select size="sm" w={120} data={years.map((y) => ({ value: String(y), label: `${y} yılı` }))} value={String(year)} onChange={(v) => { setYear(Number(v)); setPage(1); setPendingPage(1); }} leftSection={<IconCalendarStats size={16} />} allowDeselect={false} />

          <Select size="sm" w={225} clearable placeholder="Tüm lokasyonlar" data={filterOptions(locations)} value={locationId} onChange={(v) => { setLocationId(v); setPage(1); setPendingPage(1); }} leftSection={<IconBuilding size={16} />} searchable />

        </Group><Group gap="xs"><Text size="xs" c="dimmed" visibleFrom="sm">Veriler otomatik güncellenir</Text><ThemeIcon variant="light" color="teal" size={26} radius="xl"><IconCheck size={15} /></ThemeIcon></Group></Group>

      </Paper>

      {fatal && <Alert color="red" icon={<IconAlertCircle size={18} />} title="Veri yüklenemedi" mb="lg">{fatal}. Backendin çalıştığını ve portal kimlik doğrulama entegrasyonunu kontrol edin.</Alert>}

      <Tabs value={tab} onChange={setTab} variant="pills" radius="md" keepMounted={false}>

        <Tabs.List className="itb-tabs">

          <Tabs.Tab value="overview" leftSection={<IconChartBar size={18} />}>Genel Bakış</Tabs.Tab>

          <Tabs.Tab value="expenses" leftSection={<IconFileInvoice size={18} />}>Faturalar ve Giderler</Tabs.Tab>

          <Tabs.Tab value="pending" leftSection={<IconCreditCard size={18} />}>
            <Group gap={7} wrap="nowrap">Ödeme Bekleyenler
              {pendingData?.total > 0 && <Badge size="xs" color="orange" variant="light">{nfmt(pendingData.total)}</Badge>}
            </Group>
          </Tabs.Tab>

          <Tabs.Tab value="budgets" leftSection={<IconWallet size={18} />}>Bütçe Planı</Tabs.Tab>

          <Tabs.Tab value="definitions" leftSection={<IconSettings size={18} />}>Tanımlar</Tabs.Tab>

        </Tabs.List>

        <Tabs.Panel value="overview"><Overview data={dashboard} loading={isLoading} goExpenses={() => setTab('expenses')} goBudget={() => { setEditingBudget(null); setTab('budgets'); openBudget(); }} /></Tabs.Panel>

        <Tabs.Panel value="expenses"><Expenses data={expenses} loading={isLoading} categories={categories} locations={locations} categoryFilter={categoryFilter} setCategoryFilter={setCategoryFilter} statusFilter={statusFilter} setStatusFilter={setStatusFilter} search={search} setSearch={setSearch} page={page} setPage={setPage} onEdit={(e) => { setEditingExpense(e); openExpense(); }} onDelete={(e) => doDelete('expenses', e)} /></Tabs.Panel>

        <Tabs.Panel value="pending">
          <PendingPayments data={pendingData} loading={pendingLoading} error={pendingError}
            totalPendingKurus={dashboard?.pending_kurus || 0}
            search={pendingSearch} setSearch={setPendingSearch}
            page={pendingPage} setPage={setPendingPage}
            onMarkPaid={setPaymentInvoice}/>
        </Tabs.Panel>

        <Tabs.Panel value="budgets"><Group justify="flex-end" mt="md"><Button leftSection={<IconPlus size={16} />} onClick={() => { setEditingBudget(null); openBudget(); }}>Bütçe tanımla</Button></Group><Budgets data={budgets} loading={isLoading} onEdit={(b) => { setEditingBudget(b); openBudget(); }} onDelete={(b) => doDelete('budgets', b)} /></Tabs.Panel>

        <Tabs.Panel value="definitions"><Definitions categories={categories} locations={locations} onChanged={refresh} /></Tabs.Panel>

      </Tabs>

      <Text size="xs" c="dimmed" ta="center" mt={30}>BT Finans Kontrol • Tüm tutarlar Türk Lirası (₺) • Her fatura birden fazla gider kategorisine dağıtılabilir</Text>

    </Box>

    <Modal opened={!!paymentInvoice} onClose={() => { if (!paymentSaving) setPaymentInvoice(null); }}
      centered size="md" radius="lg" title="Ödeme onayı"
      closeOnEscape={!paymentSaving} closeOnClickOutside={!paymentSaving}>
      {paymentInvoice && <Stack gap="md">
        <Text size="sm">Aşağıdaki faturanın ödeme durumunu <Text component="span" fw={800} c="teal">Ödendi</Text> olarak değiştirmek istiyor musunuz?</Text>
        <Paper withBorder radius="md" p="md" bg="gray.0">
          <Stack gap={8}>
            <Group justify="space-between"><Text size="xs" c="dimmed">Fatura</Text><Text size="sm" fw={700}>{paymentInvoice.invoice_no || `#${paymentInvoice.id}`}</Text></Group>
            <Group justify="space-between"><Text size="xs" c="dimmed">Tedarikçi</Text><Text size="sm">{paymentInvoice.vendor || '—'}</Text></Group>
            <Group justify="space-between"><Text size="xs" c="dimmed">Lokasyon</Text><Text size="sm">{paymentInvoice.location_name}</Text></Group>
            <Divider/>
            <Group justify="space-between"><Text size="sm" fw={650}>Ödenecek tutar</Text><Text fw={800} size="lg" c="teal">{fmt(paymentInvoice.amount_kurus)}</Text></Group>
          </Stack>
        </Paper>
        <Text c="dimmed" size="xs">Yalnızca ödeme durumu güncellenir; fatura kalemleri, tutarı ve tarihi değişmez. İşlemi gerçekleştiren kullanıcı denetim kaydına işlenir.</Text>
        <Group justify="flex-end">
          <Button variant="default" disabled={paymentSaving} onClick={() => setPaymentInvoice(null)}>Vazgeç</Button>
          <Button color="teal" leftSection={<IconCheck size={17}/>} loading={paymentSaving} onClick={markPaid}>Evet, ödendi olarak işaretle</Button>
        </Group>
      </Stack>}
    </Modal>

    <ExpenseModal opened={expenseOpened} onClose={closeExpense} initial={editingExpense} categories={categories} locations={locations} onSaved={refresh} />

    <BudgetModal opened={budgetOpened} onClose={closeBudget} initial={editingBudget} categories={categories} locations={locations} year={year} onSaved={refresh} />

  </Box>;

}
